<template>
  <div ref="container" class="relative google-maps-input-container">
    <div class="relative">
      <TextInput
        id="map2"
        :model-value="query"
        :label="hideLabel ? '' : label"
        placeholder="Search..."
        :optional="false"
        :disabled="false"
        :show-clear-icon="true"
        type="text"
        @update:model-value="handleTextInputUpdate"
        @blur="handleBlur"
        @focus="handleFocus"
      />
    </div>

    <div
      v-if="showSuggestions"
      class="suggestions-dropdown"
      :style="{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
      }"
    >
      <ul>
        <li v-if="noResults" class="no-results-item">
          <span>No results found</span>
        </li>
        <li v-for="item in suggestions" :key="item.place_id" class="clickable-item" @click="selectPlace(item)">
          <svg
            class="location-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          <span>{{ item.description }}</span>
        </li>
      </ul>
    </div>

    <span v-if="helpText" class="text-xs text-gray-500">{{ helpText }}</span>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, onUnmounted } from 'vue';
import { usePlacesAutocomplete } from 'vue-use-places-autocomplete';
import TextInput from './TextInput.vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: [Object, String],
    default: () => ({
      city: '',
      state: '',
      country: '',
      latitude: null,
      longitude: null,
    }),
  },
  label: {
    type: String,
    default: 'Location',
  },
  autocompleteType: {
    type: String,
    default: 'address',
  },
  helpText: {
    type: String,
    required: false,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
});

const query = ref('');
const showSuggestions = ref(false);
const noResults = ref(false);
const selectedPlace = ref(false);

const isGoogleMapsLoaded = ref(false);
const checkGoogleMapsLoaded = () => {
  return typeof window !== 'undefined' && window.google && window.google.maps;
};

const { suggestions } = usePlacesAutocomplete(query, {
  debounce: 300,
  minLengthAutocomplete: 2,
  autocompletionRequest: {
    types: props.autocompleteType === 'city' ? ['(cities)'] : ['address'],
    componentRestrictions: { country: 'us' },
  },
});

const locationData = ref(props.modelValue || null);

const container = ref(null);
const dropdownPosition = ref({ top: 0, left: 0, width: 0 });

function handleTextInputUpdate(value) {
  query.value = value;
  selectedPlace.value = false;
  if (value.length >= 2) {
    updateDropdownPosition();
  } else {
    showSuggestions.value = false;
  }
}

function handleFocus() {
  if (query.value.length >= 2 && !selectedPlace.value) {
    updateDropdownPosition();
    showSuggestions.value = true;
  }
}

function handleBlur() {
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
}

onMounted(() => {
  isGoogleMapsLoaded.value = checkGoogleMapsLoaded();

  if (!isGoogleMapsLoaded.value) {
    const checkInterval = setInterval(() => {
      isGoogleMapsLoaded.value = checkGoogleMapsLoaded();
      if (isGoogleMapsLoaded.value) {
        clearInterval(checkInterval);
        initializeComponent();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      console.error('Google Maps API failed to load');
    }, 5000);
  } else {
    initializeComponent();
  }
});

function initializeComponent() {
  nextTick(() => {
    if (props.modelValue) {
      if (typeof props.modelValue === 'string') {
        query.value = props.modelValue;
      } else if (props.modelValue.city) {
        const displayValue = [props.modelValue.city, props.modelValue.state].filter(Boolean).join(', ');
        if (displayValue) {
          query.value = displayValue;
        }
      }
    }

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition);
  });
}

watch(
  () => props.modelValue,
  newValue => {
    if (typeof newValue === 'string') {
      query.value = newValue;
      locationData.value = newValue;
    } else if (newValue && newValue.city) {
      const displayValue = [newValue.city, newValue.state].filter(Boolean).join(', ');
      if (displayValue && query.value !== displayValue) {
        query.value = displayValue;
      }

      locationData.value = {
        ...newValue,
        city: newValue.city || '',
        state: newValue.state || '',
        country: newValue.country || 'USA',
        latitude: newValue.latitude !== undefined ? newValue.latitude : null,
        longitude: newValue.longitude !== undefined ? newValue.longitude : null,
      };
    }
  },
  { deep: true }
);

watch(suggestions, newSuggestions => {
  noResults.value = query.value && query.value.length >= 2 && newSuggestions.length === 0;

  if (newSuggestions.length > 0 && query.value && query.value.length >= 2 && !selectedPlace.value) {
    updateDropdownPosition();
    showSuggestions.value = true;
  }
});

function selectPlace(item) {
  query.value = item.description;

  showSuggestions.value = false;
  selectedPlace.value = true;

  if (props.autocompleteType === 'city') {
    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

      placesService.getDetails(
        {
          placeId: item.place_id,
          fields: ['geometry'],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
            locationData.value = {
              city: item.terms[0].value,
              state: item.terms[1].value,
              country: item.terms[2]?.value || 'USA',
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            };

            emit('update:modelValue', locationData.value);
          } else {
            console.error('Failed to get place details or coordinates', status);
            locationData.value = {
              city: item.terms[0].value,
              state: item.terms[1].value,
              country: item.terms[2]?.value || 'USA',
              latitude: 0,
              longitude: 0,
            };

            emit('update:modelValue', locationData.value);
          }
        }
      );
    } else {
      console.error('Google Maps Places service not available');
      locationData.value = {
        city: item.terms[0].value,
        state: item.terms[1].value,
        country: item.terms[2]?.value || 'USA',
        latitude: 0,
        longitude: 0,
      };

      emit('update:modelValue', locationData.value);
    }
  } else {
    locationData.value = item.description;
    emit('update:modelValue', locationData.value);
  }
}

function updateDropdownPosition() {
  if (container.value) {
    const rect = container.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    };
  }
}

onUnmounted(() => {
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition);
});
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
  padding: 8px;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
}

.clickable-item:hover {
  background-color: #f0f0f0;
}

.no-results-item {
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.suggestions-dropdown {
  position: fixed;
  z-index: 99999;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border-radius: 4px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08);
  list-style: none;
  padding: 0;
  /* Remove left/right positioning, will be set dynamically */
}

@media (max-width: 640px) {
  .suggestions-dropdown {
    position: fixed;
    width: 90%;
    max-height: 40vh;
    left: 5%;
    top: 30%;
    z-index: 99999;
  }
}

.location-icon {
  margin-right: 8px;
  min-width: 16px;
  color: #666;
}

.relative {
  position: relative;
  z-index: 10;
}

.relative:first-child {
  position: relative;
  z-index: 100;
}

.google-maps-input-container {
  position: relative;
  z-index: 100;
}
</style>
